import React, { useMemo, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import useUsers from './hooks/useUsers';
import {
  COMPARE_MEMBERSHIPS,
  departmentOptions,
  membershipColors,
  applyDepartmentChange,
  applyMembershipToggle,
  applySardinesChange,
  getMembershipCounts,
} from '../core/utils/compareSardinesFilterUtils';

const membershipIcons = {
  Newbie: <Icon icon="streamline:rock-and-roll-hand-remix" />,
  Junior: <Icon icon="bi:emoji-sunglasses" />,
  Senior: <Icon icon="mdi:emoticon-devil-outline" />,
};

const selectMenuProps = {
  menuShouldScrollIntoView: false,
  menuPlacement: 'auto',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

function CompareSardinesFilters({ values, onChange, idPrefix = '' }) {
  const { users, userOptions, loading: usersLoading } = useUsers();

  const membershipCounts = useMemo(() => {
    return getMembershipCounts(users, values.departments);
  }, [users, values.departments]);

  const handleDepartmentChange = useCallback((selectedOptions) => {
    onChange(applyDepartmentChange(selectedOptions, values, users));
  }, [onChange, users, values]);

  const handleMembershipToggle = useCallback((membership) => {
    onChange(applyMembershipToggle(membership, values, users));
  }, [onChange, users, values]);

  const handleSardinesChange = useCallback((selectedOptions) => {
    onChange(applySardinesChange(selectedOptions, values));
  }, [onChange, values]);

  return (
    <div className="compare-sardines-filters-form">
      <div className="compare-sardines-bulk-filters">
        <Form.Group controlId={`${idPrefix}departments`} className="form-group compare-sardines-bulk-filter">
          <label>Department</label>
          <Select
            className="compare-sardines-select"
            classNamePrefix="compare-sardines-select"
            isMulti
            isSearchable
            name="departments"
            options={departmentOptions}
            value={departmentOptions.filter((option) => values.departments.includes(option.value))}
            onChange={handleDepartmentChange}
            placeholder="Select department(s)..."
            isClearable
            isLoading={usersLoading}
            {...selectMenuProps}
          />
        </Form.Group>

        <Form.Group controlId={`${idPrefix}memberships`} className="form-group compare-sardines-bulk-filter">
          <label>Membership Type</label>
          <div className="compare-sardines-membership-filters">
            {COMPARE_MEMBERSHIPS.map((membership) => (
              <button
                key={membership}
                type="button"
                className={`filter-button ${values.memberships.includes(membership) ? 'selected' : ''}`}
                onClick={() => handleMembershipToggle(membership)}
                style={{ '--membership-color': membershipColors[membership] }}
              >
                <div className="membership-icon">{membershipIcons[membership]}</div>
                <span className="membership-name">{membership}</span>
                <span className="filter-count">{membershipCounts[membership] || 0}</span>
              </button>
            ))}
          </div>
        </Form.Group>
      </div>

      <Form.Group controlId={`${idPrefix}sardines`} className="form-group">
        <label>Sardines</label>
        <Select
          className="compare-sardines-select compare-sardines-select-sardines"
          classNamePrefix="compare-sardines-select"
          isMulti
          isSearchable
          name="sardines"
          options={userOptions}
          value={userOptions.filter((option) => values.sardines.includes(option.value))}
          onChange={handleSardinesChange}
          placeholder="Select sardines..."
          isClearable
          isLoading={usersLoading}
          {...selectMenuProps}
        />
      </Form.Group>
    </div>
  );
}

export default CompareSardinesFilters;
